import {
  resolveIdentifierColumn,
  type IdentifierColumnResolution,
  type ResolveIdentifierColumnOptions,
} from './resolveIdentifierColumn';
import { formatUnsupportedIdentifierMessage } from './unsupportedIdentifierMessage';

const QUANTITY_HEADERS = [
  'quantity',
  'qty',
  'stock',
  'units',
  'available',
  'afn-fulfillable-quantity',
];

export interface FoundInventoryIdentifierColumn {
  index: number;
  identifierType: string;
  resolution: IdentifierColumnResolution;
}

export function findInventoryIdentifierColumn(
  headers: string[],
  options: ResolveIdentifierColumnOptions = {},
): FoundInventoryIdentifierColumn | null {
  for (let i = 0; i < headers.length; i++) {
    const resolution = resolveIdentifierColumn(headers[i], options);
    if (resolution.kind !== 'unsupported') {
      return {
        index: i,
        identifierType: resolution.canonicalType,
        resolution,
      };
    }
  }
  return null;
}

export function missingInventoryIdentifierMessage(customNames: string[] = []): string {
  return formatUnsupportedIdentifierMessage(customNames);
}

export function hasInventoryQuantityColumn(headers: string[]): boolean {
  return headers.some((header) => QUANTITY_HEADERS.includes(header.trim().toLowerCase()));
}

export function validateInventoryCsvHeaders(
  rawHeaders: string[],
  options: ResolveIdentifierColumnOptions = {},
): { valid: boolean; error?: string } {
  const identifierColumn = findInventoryIdentifierColumn(rawHeaders, options);
  const hasSkuColumn = identifierColumn !== null;
  const hasQtyColumn = hasInventoryQuantityColumn(rawHeaders);

  if (!hasSkuColumn && !hasQtyColumn) {
    return {
      valid: false,
      error: 'CSV is missing both a product identifier (SKU, UPC, ASIN, etc.) and a "Quantity" column',
    };
  }
  if (!hasSkuColumn) {
    return { valid: false, error: missingInventoryIdentifierMessage(options.customNames ?? []) };
  }
  if (!hasQtyColumn) {
    return { valid: false, error: 'CSV is missing a "Quantity" column' };
  }
  return { valid: true };
}

export async function readCsvText(file: File): Promise<string> {
  const buffer = await file.arrayBuffer();
  return new TextDecoder().decode(buffer);
}

export async function readCsvHeaderChunk(file: File): Promise<string> {
  const text = await readCsvText(file);
  return text.slice(0, 10000);
}

export async function validateInventoryCsvFile(
  file: File,
  options: ResolveIdentifierColumnOptions = {},
): Promise<{ valid: boolean; error?: string }> {
  try {
    const content = await readCsvHeaderChunk(file);
    const lines = content.split(/\r?\n/).filter((line) => line.trim());

    if (lines.length === 0) {
      return { valid: false, error: 'CSV file appears to be empty' };
    }

    const rawHeaders = lines[0].split(',').map((h) => h.trim().replace(/"/g, ''));
    return validateInventoryCsvHeaders(rawHeaders, options);
  } catch {
    return { valid: false, error: 'Failed to read CSV file for validation' };
  }
}
